import { useEffect, useRef } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { applyUsercentricsPrimaryColor } from '@worldcoo/ui/lib/usercentrics';

/** The colour currently written into the CMP, with the teardown of the observers keeping it there. */
interface AppliedTheme {
	primary: string;
	onPrimary: string;
	teardown?: () => void;
}

/**
 * Repaints the cookie banner in this portal's colour.
 *
 * Reads `--primary` off its own node rather than off `:root`, because that is where the value actually
 * resolves: `DivWithPrimaryColorVariable` sets it as an inline style on a nested div (from the campaign
 * colour, else the portal's, else the portal metadata), so only a descendant inherits the right one.
 * Rendering this inside the footer's coloured wrapper means it picks up whichever of those three tiers
 * won, with no second copy of that resolution logic to keep in sync.
 *
 * Renders an empty hidden span purely as that reading point. Client-only work, in an effect, so it costs
 * the SSR pass nothing and needs no extra query.
 */
export function UsercentricsPrimaryTheme() {
	const { isInIframe } = useRouteContext({ from: '__root__' });
	const probe = useRef<HTMLSpanElement>(null);
	const applied = useRef<AppliedTheme | undefined>(undefined);

	// No dependency list: the colour is not a prop but a value inherited from an ancestor's inline style,
	// and that ancestor can change it while this component stays mounted — the NGO area's portal preview
	// repaints as the editor picks a colour, with the footer mounted throughout. Reading on every render
	// is what notices; comparing against what is already applied is what keeps it to one write per actual
	// change, so the CMP is not restyled once per render.
	useEffect(() => {
		// An embed never loads the CMP (see the `isInIframe` branch in `__root.tsx`), so there is no banner
		// to repaint and no host that could ever appear — only the `document.body` observer that waits for
		// one, on the busiest page in the app. `$campaignId.donate.tsx` renders this footer and is exactly
		// that page. Guarded inside the effect rather than by an early return so the hook count is fixed.
		if (isInIframe) return;
		const node = probe.current;
		if (!node) return;
		const styles = getComputedStyle(node);
		const primary = styles.getPropertyValue('--primary').trim();
		const onPrimary = styles.getPropertyValue('--primary-foreground').trim();
		if (applied.current?.primary === primary && applied.current.onPrimary === onPrimary) return;

		applied.current?.teardown?.();
		applied.current = { primary, onPrimary, teardown: applyUsercentricsPrimaryColor(primary, onPrimary) };
	});

	// The declaration installs observers that have to outlive the render that wrote them, so the teardown
	// belongs to the component's life and not to the effect above — which would otherwise disconnect and
	// reinstall them on every render.
	//
	// Clearing the ref is the other half, and not optional: the effect above skips when the colour matches
	// what is already applied, so leaving a torn-down entry behind means the next mount sees "already
	// applied", returns early, and never reinstalls the observers. StrictMode's mount/unmount/mount is
	// exactly that sequence with an unchanged colour, so without this the banner is never themed in dev.
	useEffect(
		() => () => {
			applied.current?.teardown?.();
			applied.current = undefined;
		},
		[],
	);

	return <span ref={probe} hidden />;
}
